import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { localize } from '../i18n/script';
import { bookingApi } from '../services/bookingApi';
import { trackPurchase } from '../utils/analytics';
import { forgetMemberCode } from '../utils/memberCode';
import { clearUtmSource } from '../utils/utmTracking';

interface BookingDetails {
  bookingId: string;
  activityTitle: string;
  activityTitleCN?: string | null;
  activityTitleEN?: string | null;
  sessionDate: string;
  sessionTime: string;
  totalAmount: number;
  participantsCount: number;
  email: string;
  discountAmount?: number;
  promotionCodeSnapshot?: string;
  memberDiscountAmount?: number;
  memberPromotionCodeSnapshot?: string;
}

export default function BookingSuccess() {
  const { t, i18n } = useTranslation('booking');
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('bookingId');
  const legacyEmail = searchParams.get('email'); // For backward compatibility
  const navigate = useNavigate();
  const { isAuthenticated, getToken } = useAuth();

  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(!!sessionId || !!bookingId);
  const [error, setError] = useState<string | null>(null);

  // Path A: Stripe purchase — session_id in the URL
  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!sessionId) return;

      try {
        setLoading(true);
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        const response = await fetch(`${apiUrl}/payments/checkout-session/${sessionId}`);

        if (!response.ok) {
          throw new Error('Failed to fetch booking details');
        }

        const result = await response.json();
        if (result.success && result.data) {
          setBooking(result.data);
          // GA4: track purchase conversion
          trackPurchase({
            type: 'booking',
            transactionId: result.data.bookingId,
            value: result.data.totalAmount ? result.data.totalAmount / 100 : undefined,
            title: result.data.activityTitle,
          });
          clearUtmSource();
          forgetMemberCode();
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch booking details';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [sessionId]);

  // Path B: Credit-only / pass-booking — bookingId in the URL (no Stripe session)
  useEffect(() => {
    const fetchBookingById = async () => {
      if (sessionId || !bookingId) return;

      try {
        setLoading(true);
        const token = await getToken();
        if (!token) return;
        const data = await bookingApi.getBookingById(bookingId, token);
        const activityTitle = localize(
          data.session.activity.titleCN,
          data.session.activity.titleEN,
          i18n.language
        );
        setBooking({
          bookingId: data.id,
          activityTitle,
          sessionDate: data.session.startTime,
          sessionTime: '',
          totalAmount: data.totalAmount,
          participantsCount: data.participants.length,
          email: '',
          discountAmount: data.discountAmount ?? undefined,
          promotionCodeSnapshot: data.promotionCodeSnapshot ?? undefined,
          memberDiscountAmount: data.memberDiscountAmount ?? undefined,
          memberPromotionCodeSnapshot: data.memberPromotionCodeSnapshot ?? undefined,
        });
        // GA4: track purchase conversion
        trackPurchase({
          type: 'booking',
          transactionId: data.id,
          value: data.totalAmount ? data.totalAmount / 100 : undefined,
          title: activityTitle,
        });
        clearUtmSource();
        forgetMemberCode();
      } catch {
        // Non-critical — still show generic success
      } finally {
        setLoading(false);
      }
    };

    fetchBookingById();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language intentionally excluded to prevent re-firing on language switch
  }, [bookingId, sessionId, getToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">{t('success.loadingDetails')}</p>
        </div>
      </div>
    );
  }

  const displayEmail = booking?.email || legacyEmail || '';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {/* Success Icon */}
        <div className="mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        {/* Success Message */}
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('success.title')}</h1>

        <div className="space-y-4 text-gray-600 mb-8">
          <p>{t('success.thankYou')}</p>

          {booking && (
            <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2">
              {booking.activityTitle && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('success.activity')}:</span>
                  <span className="font-medium">
                    {/* The API labels Stripe-side things English first; the page follows the site language. */}
                    {localize(booking.activityTitleCN, booking.activityTitleEN, i18n.language) ||
                      booking.activityTitle}
                  </span>
                </div>
              )}
              {booking.participantsCount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('success.participants')}:</span>
                  <span className="font-medium">{booking.participantsCount}</span>
                </div>
              )}
              {booking.totalAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t('success.totalPaid')}:</span>
                  <span className="font-medium text-green-600">
                    ${(booking.totalAmount / 100).toFixed(2)}
                  </span>
                </div>
              )}
              {booking.discountAmount && booking.discountAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">
                    {t('promotionCode.discountLabel')}
                    {booking.promotionCodeSnapshot && (
                      <span className="ml-1 text-green-600">({booking.promotionCodeSnapshot})</span>
                    )}
                  </span>
                  <span className="font-medium text-green-600">
                    -${(booking.discountAmount / 100).toFixed(2)}
                  </span>
                </div>
              )}
              {booking.memberDiscountAmount && booking.memberDiscountAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">
                    {t('promotionCode.inviteLabel')}
                    {booking.memberPromotionCodeSnapshot && (
                      <span className="ml-1 text-green-600">
                        ({booking.memberPromotionCodeSnapshot})
                      </span>
                    )}
                  </span>
                  <span className="font-medium text-green-600">
                    -${(booking.memberDiscountAmount / 100).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          )}

          {displayEmail && (
            <div className="pt-4">
              <p className="text-sm">{t('success.confirmationEmailSent')}</p>
              <p className="font-semibold text-gray-900 mt-1">{displayEmail}</p>
              <p className="text-xs text-gray-500 mt-2">{t('success.checkSpam')}</p>
            </div>
          )}

          {error && (
            <div className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-lg">
              {t('success.unableToLoad')}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {isAuthenticated ? (
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors"
            >
              {t('success.myActivities')}
            </button>
          ) : (
            <>
              {/* Guest Registration Prompt */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left mb-3">
                <p className="text-sm font-semibold text-blue-900 mb-1">
                  {t('success.registerPromptTitle')}
                </p>
                <p className="text-xs text-blue-800">{t('success.registerPromptMessage')}</p>
              </div>

              <button
                onClick={() => navigate('/register')}
                className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors"
              >
                {t('success.createAccount')}
              </button>

              <button
                onClick={() => navigate('/')}
                className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:border-gray-400 transition-colors"
              >
                {t('success.browseActivities')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
